import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { FileText, Play, Pause, Download, Image as ImageIcon, Film, Mic, ChevronDown, ChevronUp, Forward as ForwardIcon } from 'lucide-react';
import type { TeamChatMessage, MessageAttachment } from '../../lib/stores/teamChatStore';
import { LinkPreview } from './LinkPreview';
import { AudioWaveform } from './AudioWaveform';
import { TaskCommentCard } from './TaskCommentCard';
import { CrmShareCard } from './CrmShareCard';
import { AUDIO_TRANSCRIPTION_LEGACY_ENABLED } from '@/lib/featureFlags';

interface MessageContentProps {
  message: TeamChatMessage;
  isOwn: boolean;
  isEdited?: boolean;
  onImageClick?: (url: string, name?: string) => void;
}

// Format bytes to human-readable size
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Format seconds to mm:ss
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Extract URLs from text
const extractUrls = (text: string): string[] => {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const matches = text.match(urlRegex);
  return matches || [];
};

// Check if text contains any markdown formatting
const hasMarkdown = (text: string): boolean =>
  /(\*\*|__|\*(?!\s)|_(?!\s)|~~|`|^#{1,3}\s|^[-*+]\s|^\d+\.\s)/m.test(text);

// Regex menzioni: supporta @[Nome Cognome] (nuovo) e @NomeCognome (legacy)
const MENTION_REGEX = /@\[([^\]]+)\]|@(\w+)/g;

// Render inline text with @mentions highlighted
const renderInlineWithMentions = (text: string, isOwn: boolean): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  const regex = new RegExp(MENTION_REGEX.source, 'g');

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const name = match[1] || match[2]; // [1] = nuovo formato, [2] = legacy
    parts.push(
      <span key={`m-${i}`} className="font-semibold" style={{ color: isOwn ? '#B4D7FF' : '#2383E2' }}>
        @{name}
      </span>
    );
    lastIndex = match.index + match[0].length;
    i++;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length <= 1 ? (parts[0] ?? text) : <>{parts}</>;
};

// Markdown components with mention support and own-message styling
const getMarkdownComponents = (isOwn: boolean): Components => ({
  p: ({ children }) => <p className="mb-0 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through">{children}</del>,
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <code className={`block text-[12px] px-3 py-2 rounded-md my-1 font-mono overflow-x-auto ${className || ''}`}
        style={{ backgroundColor: isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(55,53,47,0.08)' }}>
        {children}
      </code>
    ) : (
      <code className="text-[12px] px-1 py-0.5 rounded font-mono"
        style={{ backgroundColor: isOwn ? 'rgba(255,255,255,0.2)' : 'rgba(55,53,47,0.1)' }}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-1">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="underline hover:opacity-80" style={{ color: isOwn ? '#B4D7FF' : '#2383E2' }}>
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc ml-4 my-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-4 my-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-[13px]">{children}</li>,
  // Block headings/images/hr — disabilitati per sicurezza
  h1: ({ children }) => <p className="font-bold">{children}</p>,
  h2: ({ children }) => <p className="font-bold">{children}</p>,
  h3: ({ children }) => <p className="font-bold">{children}</p>,
  hr: () => null,
  img: () => null,
});

// Detect if content is a single GIF URL (Tenor, Giphy, etc.)
const isGifUrl = (text: string): boolean => {
  const trimmed = text.trim();
  if (/\s/.test(trimmed)) return false; // must be a single URL, no spaces
  try {
    const url = new URL(trimmed);
    // Tenor media URLs
    if (url.hostname.includes('tenor.com')) return true;
    // Giphy media URLs
    if (url.hostname.includes('giphy.com')) return true;
    // Direct .gif links
    if (url.pathname.endsWith('.gif')) return true;
    return false;
  } catch { return false; }
};

// Render text: markdown + @mentions + URLs
const renderTextContent = (text: string, isOwn: boolean) => {
  // Se contiene markdown, usa react-markdown
  if (hasMarkdown(text)) {
    // Pre-processa menzioni: @[Nome] → **@Nome**, @Nome → **@Nome**
    const processed = text.replace(
      /@\[([^\]]+)\]|@(\w+)/g,
      (_m, bracketed, legacy) => `**@${bracketed || legacy}**`
    );
    return <ReactMarkdown components={getMarkdownComponents(isOwn)}>{processed}</ReactMarkdown>;
  }

  // Nessun markdown: rendering veloce con menzioni e URL
  const mentionRegex = new RegExp(MENTION_REGEX.source, 'g');
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts: { start: number; end: number; type: 'mention' | 'url'; value: string; display: string }[] = [];

  let match: RegExpExecArray | null;
  while ((match = mentionRegex.exec(text)) !== null) {
    const name = match[1] || match[2];
    parts.push({ start: match.index, end: match.index + match[0].length, type: 'mention', value: match[0], display: `@${name}` });
  }
  while ((match = urlRegex.exec(text)) !== null) {
    parts.push({ start: match.index, end: match.index + match[0].length, type: 'url', value: match[0], display: match[0] });
  }
  parts.sort((a, b) => a.start - b.start);

  if (parts.length === 0) return <span>{text}</span>;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  parts.forEach((part, i) => {
    if (part.start > lastIndex) result.push(<span key={`t-${i}`}>{text.slice(lastIndex, part.start)}</span>);
    if (part.type === 'mention') {
      result.push(<span key={`m-${i}`} className="font-semibold" style={{ color: isOwn ? '#B4D7FF' : '#2383E2' }}>{part.display}</span>);
    } else {
      result.push(<a key={`u-${i}`} href={part.value} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80" style={{ color: isOwn ? '#B4D7FF' : '#2383E2' }}>{part.display}</a>);
    }
    lastIndex = part.end;
  });
  if (lastIndex < text.length) result.push(<span key="tail">{text.slice(lastIndex)}</span>);
  return <>{result}</>;
};

const EditedSuffix = () => (
  <span className="text-[10px] ml-1 opacity-60 italic">(mod.)</span>
);

// --- Attachment renderers ---

const ImageAttachment: React.FC<{ att: MessageAttachment; onImageClick?: (url: string, name?: string) => void }> = ({ att, onImageClick }) => (
  <button
    onClick={() => onImageClick ? onImageClick(att.url, att.name) : window.open(att.url, '_blank')}
    className="block text-left"
  >
    <img
      src={att.url}
      alt={att.name}
      className="rounded-lg max-w-[260px] max-h-[200px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
    />
  </button>
);

const VideoAttachment: React.FC<{ att: MessageAttachment }> = ({ att }) => (
  <video
    src={att.url}
    controls
    className="rounded-lg max-w-[260px] max-h-[200px]"
    preload="metadata"
  />
);

const AudioAttachment: React.FC<{ att: MessageAttachment; isOwn: boolean }> = ({ att, isOwn }) => {
  const [showTranscription, setShowTranscription] = useState(false);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Mic className="w-4 h-4 flex-shrink-0" style={{ color: isOwn ? '#B4D7FF' : '#787774' }} strokeWidth={1.5} />
        <audio src={att.url} controls className="h-8 max-w-[220px]" preload="metadata" />
        {att.duration != null && (
          <span className="text-[10px] opacity-60">{formatDuration(att.duration)}</span>
        )}
      </div>
      {AUDIO_TRANSCRIPTION_LEGACY_ENABLED && att.transcription && (
        <div>
          <button
            onClick={() => setShowTranscription(!showTranscription)}
            className="flex items-center gap-1 text-[11px] hover:opacity-80 transition-opacity"
            style={{ color: isOwn ? '#B4D7FF' : '#2383E2' }}
          >
            {showTranscription ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Trascrizione
          </button>
          {showTranscription && (
            <p className="text-[12px] mt-1 px-2 py-1.5 rounded-md italic leading-relaxed" style={{ backgroundColor: 'rgba(55,53,47,0.06)' }}>
              {att.transcription}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const FileAttachment: React.FC<{ att: MessageAttachment; isOwn: boolean }> = ({ att, isOwn }) => (
  <a
    href={att.url}
    target="_blank"
    rel="noopener noreferrer"
    download={att.name}
    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:opacity-80 transition-opacity"
    style={{ backgroundColor: 'rgba(55,53,47,0.06)' }}
  >
    <FileText className="w-5 h-5 flex-shrink-0" style={{ color: isOwn ? '#B4D7FF' : '#787774' }} strokeWidth={1.5} />
    <div className="flex flex-col min-w-0">
      <span className="text-[13px] font-medium truncate max-w-[180px]">{att.name}</span>
      {att.size != null && (
        <span className="text-[10px] opacity-50">{formatFileSize(att.size)}</span>
      )}
    </div>
    <Download className="w-4 h-4 flex-shrink-0 ml-auto opacity-50" strokeWidth={1.5} />
  </a>
);

// --- WhatsApp Forward Card ---

interface WaForwardMessage {
  id: string;
  content: string;
  type: string;
  direction: 'inbound' | 'outbound';
  senderName: string;
  timestamp: string;
  mediaUrl?: string;
  mediaType?: string;
}

function getMessageTypeLabel(type: string): string {
  switch (type) {
    case 'image': return '📷 Immagine';
    case 'video': return '🎥 Video';
    case 'audio': case 'ptt': return '🎵 Audio';
    case 'document': return '📄 Documento';
    case 'sticker': return '🏷️ Sticker';
    case 'location': return '📍 Posizione';
    case 'contact': return '👤 Contatto';
    default: return '';
  }
}

function formatWaTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

const WhatsAppForwardCard: React.FC<{ content: string }> = ({ content }) => {
  let parsed: Record<string, unknown> | null = null;
  try {
    const obj = JSON.parse(content);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) parsed = obj;
  } catch { /* non è JSON, testo libero */ }

  const whatsAppIcon = (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current flex-shrink-0">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.243-1.214l-.257-.154-2.786.828.828-2.786-.154-.257A8 8 0 1112 20z"/>
    </svg>
  );

  // === NUOVO FORMATO: multi-messaggio con campo `messages` ===
  if (parsed && Array.isArray(parsed.messages)) {
    const msgs = parsed.messages as WaForwardMessage[];
    const chatName = (parsed.chatName as string) || 'WhatsApp';
    const chatId = parsed.chatId as string;

    return (
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(37,211,102,0.3)', backgroundColor: 'rgba(37,211,102,0.06)' }}>
        {/* Header verde con branding WA */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium" style={{ backgroundColor: 'rgba(37,211,102,0.12)', color: '#25D366' }}>
          {whatsAppIcon}
          <span className="truncate">Chat WhatsApp · {chatName}</span>
          <span className="ml-auto flex-shrink-0 text-[10px] opacity-70">
            {msgs.length} {msgs.length === 1 ? 'messaggio' : 'messaggi'}
          </span>
        </div>

        {/* Lista messaggi */}
        <div className="divide-y" style={{ borderColor: 'rgba(37,211,102,0.1)' }}>
          {msgs.map((m, i) => {
            const isOutbound = m.direction === 'outbound';
            const typeLabel = getMessageTypeLabel(m.type);
            const displayContent = typeLabel || m.content || '';

            return (
              <div
                key={m.id || i}
                className="px-3 py-1.5"
                style={{ backgroundColor: isOutbound ? 'rgba(37,211,102,0.04)' : 'transparent' }}
              >
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-[11px] font-semibold flex-shrink-0"
                    style={{ color: isOutbound ? '#2383E2' : '#25D366' }}
                  >
                    {isOutbound ? 'Tu' : m.senderName}
                  </span>
                  <span className="text-[10px] flex-shrink-0" style={{ color: '#9B9A97' }}>
                    {formatWaTime(m.timestamp)}
                  </span>
                </div>
                <div className="text-[12px] leading-relaxed whitespace-pre-wrap" style={{ color: '#37352F' }}>
                  {displayContent}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer: link alla chat WhatsApp */}
        {chatId && (
          <a
            href={`/whatsapp?chat=${encodeURIComponent(chatId)}`}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors hover:bg-[rgba(37,211,102,0.08)]"
            style={{ borderTop: '1px solid rgba(37,211,102,0.15)', color: '#2383E2' }}
          >
            Apri chat WhatsApp →
          </a>
        )}
      </div>
    );
  }

  // === FORMATO LEGACY: classificazione AI (title/description/request_type) ===
  if (parsed && (parsed.title || parsed.description || parsed.request_type)) {
    const title = parsed.title as string || '';
    const description = parsed.description as string || '';
    const originalText = parsed.original_text as string || '';
    const senderName = parsed.sender_name as string || '';
    const requestType = parsed.request_type as string || '';

    const typeLabels: Record<string, { label: string; color: string; bg: string }> = {
      feature_request: { label: 'Feature Request', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
      bug_report: { label: 'Bug Report', color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
      feedback: { label: 'Feedback', color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
      question: { label: 'Domanda', color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
    };
    const typeInfo = typeLabels[requestType] || { label: requestType, color: '#6B7280', bg: 'rgba(107,114,128,0.08)' };

    return (
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(37,211,102,0.3)', backgroundColor: 'rgba(37,211,102,0.06)' }}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium" style={{ backgroundColor: 'rgba(37,211,102,0.12)', color: '#25D366' }}>
          {whatsAppIcon}
          Inoltro da WhatsApp
        </div>
        <div className="px-3 py-2 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {requestType && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: typeInfo.color, backgroundColor: typeInfo.bg }}>
                {typeInfo.label}
              </span>
            )}
            {senderName && (
              <span className="text-[11px] text-gray-500">da {senderName}</span>
            )}
          </div>
          {title && (
            <div className="text-[13px] font-semibold leading-snug" style={{ color: '#37352F' }}>{title}</div>
          )}
          {description && (
            <div className="text-[12px] leading-relaxed" style={{ color: '#555' }}>{description}</div>
          )}
          {originalText && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-gray-400 hover:text-gray-600 select-none">Messaggio originale</summary>
              <div className="mt-1 p-2 rounded text-[11px] leading-relaxed whitespace-pre-wrap" style={{ backgroundColor: 'rgba(0,0,0,0.03)', color: '#666' }}>
                {originalText}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }

  // === FALLBACK: testo libero con pattern "Da: NomeSender\n..." ===
  let sender = '';
  let body = content;
  const senderMatch = content.match(/^Da:\s*(.+?)(?:\n|$)/);
  if (senderMatch) {
    sender = senderMatch[1].trim();
    body = content.slice(senderMatch[0].length).trim();
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(37,211,102,0.3)', backgroundColor: 'rgba(37,211,102,0.06)' }}>
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium" style={{ backgroundColor: 'rgba(37,211,102,0.12)', color: '#25D366' }}>
        {whatsAppIcon}
        Inoltro da WhatsApp
      </div>
      <div className="px-3 py-2">
        {sender && (
          <div className="text-[11px] font-semibold mb-1" style={{ color: '#37352F' }}>{sender}</div>
        )}
        <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#37352F' }}>
          {body}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

export const MessageContent: React.FC<MessageContentProps> = ({ message, isOwn, isEdited, onImageClick }) => {
  // Deleted message
  if (message.is_deleted) {
    return (
      <span className="text-[13px] italic opacity-50">Messaggio eliminato</span>
    );
  }

  const messageType = message.message_type || 'text';
  const attachment = message.attachments?.[0];
  const isForwarded = !!(message as any).forwarded_from_id;

  const editedSuffix = isEdited ? <EditedSuffix /> : null;
  const forwardedBadge = isForwarded ? (
    <div className="flex items-center gap-1 text-[10px] italic mb-1" style={{ color: isOwn ? 'rgba(255,255,255,0.6)' : '#9B9A97' }}>
      <ForwardIcon className="w-3 h-3" strokeWidth={1.5} />
      Messaggio inoltrato
    </div>
  ) : null;

  // Link preview per messaggi di testo con URL
  const urls = messageType === 'text' ? extractUrls(message.content || '') : [];
  const linkPreviews = urls.length > 0 ? (
    <div className="mt-1">
      {urls.slice(0, 2).map((url, i) => (
        <LinkPreview key={i} url={url} isOwn={isOwn} />
      ))}
    </div>
  ) : null;

  switch (messageType) {
    case 'image':
      return (
        <div className="flex flex-col gap-1">
          {forwardedBadge}
          {attachment && <ImageAttachment att={attachment} onImageClick={onImageClick} />}
          {message.content && (
            <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {renderTextContent(message.content, isOwn)}
            </div>
          )}
          {editedSuffix}
        </div>
      );

    case 'video':
      return (
        <div className="flex flex-col gap-1">
          {forwardedBadge}
          {attachment && <VideoAttachment att={attachment} />}
          {message.content && (
            <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {renderTextContent(message.content, isOwn)}
            </div>
          )}
          {editedSuffix}
        </div>
      );

    case 'audio':
      return (
        <div className="flex flex-col gap-1">
          {forwardedBadge}
          {attachment && <AudioWaveform att={attachment} isOwn={isOwn} />}
          {message.content && (
            <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {renderTextContent(message.content, isOwn)}
            </div>
          )}
          {editedSuffix}
        </div>
      );

    case 'file':
      return (
        <div className="flex flex-col gap-1">
          {forwardedBadge}
          {attachment && <FileAttachment att={attachment} isOwn={isOwn} />}
          {message.content && (
            <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {renderTextContent(message.content, isOwn)}
            </div>
          )}
          {editedSuffix}
        </div>
      );

    case 'whatsapp_forward':
      return (
        <div className="flex flex-col gap-1">
          <WhatsAppForwardCard content={message.content} />
          {editedSuffix}
        </div>
      );

    case 'task_comment':
      return (
        <div className="flex flex-col gap-1">
          <TaskCommentCard content={message.content} />
          {editedSuffix}
        </div>
      );

    case 'crm_share':
      return (
        <div className="flex flex-col gap-1">
          <CrmShareCard content={message.content} />
          {editedSuffix}
        </div>
      );

    case 'text':
    default:
      // GIF URL → render as inline image, no link preview
      if (isGifUrl(message.content)) {
        return (
          <div className="flex flex-col gap-1">
            {forwardedBadge}
            <img
              src={message.content.trim()}
              alt="GIF"
              className="rounded-lg max-w-[260px] max-h-[200px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onImageClick ? onImageClick(message.content.trim(), 'GIF') : window.open(message.content.trim(), '_blank')}
            />
            {editedSuffix}
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-1">
          {forwardedBadge}
          <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
            {renderTextContent(message.content, isOwn)}
            {editedSuffix}
          </div>
          {linkPreviews}
        </div>
      );
  }
};
